import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Homepage carousel with real swipe, drag feedback, and only ±1 slide mounted.
 * Photos sit matted on white (object‑fit: contain in a 4:5 frame).
 */
export default function MobileCarousel({ items }) {
  const [index, setIndex] = useState(0);
  const [drag, setDrag] = useState(0);
  const [animate, setAnimate] = useState(true);
  const touch = useRef(null);
  const count = items.length;

  const clamp = useCallback((i) => (i + count) % count, [count]);

  const go = useCallback(
    (dir) => {
      setAnimate(true);
      setIndex((i) => clamp(i + dir));
    },
    [clamp]
  );

  // Preload neighbours
  useEffect(() => {
    if (count < 2) return;
    [clamp(index + 1), clamp(index - 1)].forEach((i) => {
      const img = new Image();
      img.src = items[i].url;
    });
  }, [index, items, count, clamp]);

  if (!count) return null;

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setAnimate(false);
  };

  const onTouchMove = (e) => {
    if (!touch.current) return;
    const dx = e.touches[0].clientX - touch.current.x;
    const dy = e.touches[0].clientY - touch.current.y;
    if (Math.abs(dx) > Math.abs(dy)) setDrag(dx);
  };

  const onTouchEnd = () => {
    const dx = drag;
    touch.current = null;
    setAnimate(true);
    setDrag(0);
    if (Math.abs(dx) > 55) go(dx < 0 ? 1 : -1);
  };

  const current = items[index];

  return (
    <div className="carousel" aria-roledescription="carousel">
      <div className="carousel-viewport">
        <div
          className="carousel-track"
          style={{
            transform: `translateX(calc(${-index * 100}% + ${drag}px))`,
            transition: animate ? "transform 0.3s ease-out" : "none",
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {items.map((item, i) => {
            const near = Math.abs(i - index) <= 1 || count <= 3;
            return (
              <div
                className="carousel-slide"
                key={item.name || i}
                aria-hidden={i !== index}
              >
                {near && <img src={item.url} alt={item.caption || ""} draggable={false} />}
              </div>
            );
          })}
        </div>

        {count > 1 && (
          <>
            <button
              className="carousel-arrow carousel-arrow--left"
              aria-label="Previous photograph"
              onClick={() => go(-1)}
            >
              &#8592;
            </button>
            <button
              className="carousel-arrow carousel-arrow--right"
              aria-label="Next photograph"
              onClick={() => go(1)}
            >
              &#8594;
            </button>
          </>
        )}
      </div>

      <div className="carousel-meta">
        <span className="carousel-caption">{current.caption}</span>
        <span className="carousel-counter">
          {index + 1} / {count}
        </span>
      </div>
    </div>
  );
}